import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Avatar, Button, Stack, Chip, CircularProgress } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { FiSend } from "react-icons/fi";
import { theme } from "../../theme/colors";
import ShareProfileModal from "../AlertComponents/ShareProfileModal";
import { buildProfileShareUrl } from "../../utils/profileShareUrl";

export default function ProfileHeader({
  formData,
  profileImage,
  uploadingImage,
  onImageUpload,
  accountTypeName,
  userId,
  onToast,
}) {
  const fileInputRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageUpload(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const isOrg = ["organization", "non_profit", "municipality"].includes(accountTypeName);
  const displayName = isOrg && formData.entity_name ? formData.entity_name : `${formData.first_name} ${formData.last_name}`.trim();
  const displayUsername = formData.username ? `@${formData.username}` : "";
  const profileUrl = buildProfileShareUrl({
    username: formData.username,
    userId,
  });
  const viewProfilePath = profileUrl
    ? profileUrl.replace(window.location.origin, "")
    : "#";

  const accountTypeLabel = accountTypeName
    ? String(accountTypeName).replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
    : "Civix Account";

  const handleShareProfile = () => {
    if (!profileUrl) {
      onToast?.("Profile link isn't ready yet. Please try again.", "error");
      return;
    }
    setShareOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, gap: 3, mb: 4 }}>
      <Box sx={{ position: "relative" }}>
        <Avatar
          src={profileImage}
          sx={{
            width: 100,
            height: 100,
            border: `3px solid ${theme.primary}`,
            bgcolor: "#f1f5f9",
            color: theme.primary,
            fontSize: "2rem",
            fontWeight: 700,
          }}
        >
          {!profileImage && displayName ? displayName.charAt(0).toUpperCase() : ""}
        </Avatar>
        {uploadingImage && (
          <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", borderRadius: "50%" }}>
            <CircularProgress size={24} sx={{ color: theme.primary }} />
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" } }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#111" }}>
          {displayName || "Your Name"}
        </Typography>
        {displayUsername && (
          <Typography variant="body1" sx={{ color: "#0f172a", mb: 1 }}>
            {displayUsername}
          </Typography>
        )}
        <Chip
          size="small"
          label={accountTypeLabel}
          sx={{
            fontWeight: 700,
            backgroundColor: `${theme.primary}12`,
            color: "#0f172a",
            mb: 2,
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "center", sm: "flex-start" }}>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: theme.gradient,
              color: "#fff",
              borderRadius: 2,
              "&:hover": { opacity: 0.95, background: theme.gradient },
            }}
          >
            Change Profile Photo
          </Button>
          <Button
            component={Link}
            to={viewProfilePath}
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#e2e8f0",
              color: "#0f172a",
              borderRadius: 2,
              bgcolor: "#fff",
              "&:hover": { borderColor: theme.primary, color: theme.primary, bgcolor: "#f8fafc" },
            }}
          >
            View profile
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleShareProfile}
            startIcon={<FiSend size={16} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#e2e8f0",
              color: "#0f172a",
              borderRadius: 2,
              bgcolor: "#fff",
              "&:hover": { borderColor: theme.primary, color: theme.primary, bgcolor: "#f8fafc" },
            }}
          >
            Share profile
          </Button>
        </Stack>
      </Box>

      <ShareProfileModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        profileUrl={profileUrl}
        displayName={displayName}
        title="Share your profile"
        footerNote={
          formData.is_profile_public
            ? null
            : "Your profile is private, so only signed-in members can open this link. Turn on “Make my profile public” below if you want anyone with the link to open it."
        }
        onCopied={() => onToast?.("Profile link copied", "success")}
        onCopyFailed={() => onToast?.("Couldn't copy the link. Please try again.", "error")}
      />
    </Box>
  );
}
